import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { XMarkIcon, DocumentPlusIcon } from '@heroicons/react/24/solid';
import { storeFeedbackByAdmin } from '@/store/slices/feedbackSlice';
import { fetchCountryMobileCode } from '@/store/slices/internalUsersSlice';
import Toaster from '@/components/Toaster';
import {
  CountryOption,
  CountrySingleValue,
} from '@/components/CountrySelectComponents';
import { useTranslation } from 'react-i18next';

// Same validation regex used in AddInternalUser.jsx
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const MAX_IMAGES = 10;

const schema = (t) => yup.object({
  first_name: yup
    .string()
    .trim()
    .required(t('validation.first_name_required'))
    .matches(notBlankRegex, t('validation.invalid_first_name'))
    .matches(noHTMLRegex, t('validation.html_not_allowed'))
    .matches(noCodeRegex, t('validation.code_not_allowed'))
    .matches(noConsecutiveSpecialChars, t('validation.no_consecutive_special_chars'))
    .matches(noLeadingTrailingPunct, t('validation.no_leading_trailing_punctuation'))
    .min(2, t('validation.first_name_min'))
    .max(30, t('validation.first_name_max')),

  last_name: yup
    .string()
    .trim()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .matches(noHTMLRegex, t('validation.html_not_allowed'))
    .matches(noCodeRegex, t('validation.code_not_allowed'))
    .max(30, t('validation.last_name_max')),

  email: yup
    .string()
    .trim()
    .required(t('validation.email_required'))
    .email(t('validation.invalid_email'))
    .matches(notBlankRegex, t('validation.invalid_email'))
    .matches(noHTMLRegex, t('validation.html_not_allowed'))
    .matches(noCodeRegex, t('validation.code_not_allowed'))
    .max(50, t('validation.email_max')),

  country_code: yup.string().trim().required(t('validation.country_code_required')),

  mobile_number: yup
    .string()
    .trim()
    .required(t('validation.mobile_number_required'))
    .matches(notBlankRegex, t('validation.invalid_mobile_number'))
    .matches(/^\d+$/, t('validation.only_digits_allowed'))
    .min(8, t('validation.mobile_number_min'))
    .max(15, t('validation.mobile_number_max'))
    .test('not-all-zeros', t('validation.mobile_number_not_zeros'), (v) => !/^0+$/.test(v)),

  // FIXED: company name is now required
  registered_company_name: yup
    .string()
    .trim()
    .required(t('validation.company_name_required'))
    .matches(noHTMLRegex, t('validation.html_not_allowed'))
    .matches(noCodeRegex, t('validation.code_not_allowed'))
    .max(100, t('validation.company_name_max')),

  star_rating: yup.string().required(t('validation.rating_required')),

  feedback_proof_file: yup
    .mixed()
    .required(t('validation.proof_file_required'))
    .test('fileSize', t('validation.file_size_max_10mb'), (value) => {
      return value && value.size <= 10 * 1024 * 1024;
    })
    .test(
      'fileType',
      t('validation.allowed_file_types_proof'),
      (value) => {
        return (
          value &&
          ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(
            value.type
          )
        );
      }
    ),

  description: yup
    .string()
    .trim()
    .required(t('validation.description_required'))
    .matches(noHTMLRegex, t('validation.html_not_allowed'))
    .matches(noCodeRegex, t('validation.code_not_allowed'))
    .min(5, t('validation.description_min'))
    .max(250, t('validation.description_max')),
});

const defaultValues = {
  first_name: '',
  last_name: '',
  email: '',
  country_code: '+91',
  mobile_number: '',
  registered_company_name: '',
  star_rating: '5',
  description: '',
  feedback_proof_file: null,
};

const UploadFeedbackModal = ({ open, onClose, rfq_id, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('order');
  const { mobile_country_code } = useSelector((state) => state.internalUser);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues,
  });

  const [images, setImages] = useState([]);
  const [proofPreview, setProofPreview] = useState(null);

  useEffect(() => {
    dispatch(fetchCountryMobileCode());
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setImages([]);
      setProofPreview(null);
    }
  }, [open, reset]);

  if (!open) return null;

  // FIXED: cap total images at MAX_IMAGES
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    if (files.length === 0) return;

    setImages((prev) => {
      const remainingSlots = MAX_IMAGES - prev.length;

      if (remainingSlots <= 0) {
        Toaster.error(t('toaster.max_images_reached', { max: MAX_IMAGES }));
        return prev;
      }

      if (files.length > remainingSlots) {
        Toaster.error(
          t('toaster.max_images_exceeded', { max: MAX_IMAGES, remaining: remainingSlots })
        );
      }

      return [...prev, ...files.slice(0, remainingSlots)];
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Client-side validation
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      Toaster.error(t('toaster.allowed_file_types_proof'));
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      Toaster.error(t('toaster.file_size_max_10mb'));
      e.target.value = '';
      return;
    }

    setValue('feedback_proof_file', file, { shouldValidate: true });

    if (file.type === 'application/pdf') {
      setProofPreview(null);
    } else {
      setProofPreview(URL.createObjectURL(file));
    }
  };

  const submitFeedback = async (data) => {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value ?? '');
      });

      formData.append('rfq_id', rfq_id);

      images.forEach((file) => {
        formData.append('feedback_images[]', file);
      });

      if (data.feedback_proof_file) {
        formData.append('feedback_proof_file', data.feedback_proof_file);
      }

      const result = await dispatch(storeFeedbackByAdmin(formData));

      if (storeFeedbackByAdmin.fulfilled.match(result)) {
        Toaster.success(t('toaster.feedback_upload_success'));
        onSuccess?.();
        onClose();
      } else {
        Toaster.error(result.payload?.message || t('toaster.feedback_upload_failed'));
      }
    } catch (err) {
      Toaster.error(t('toaster.feedback_upload_error'));
    }
  };

  const onSubmit = (data) => {
    Swal.fire({
      title: t('swal.submit_feedback_title'),
      text: t('swal.submit_feedback_text'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('swal.yes_submit'),
      cancelButtonText: t('swal.cancel'),
      confirmButtonColor: '#000000',
      cancelButtonColor: '#9ca3af',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '10000';
      },
    }).then((result) => {
      if (result.isConfirmed) {
        submitFeedback(data);
      }
    });
  };

  const descriptionValue = watch('description') || '';

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-800">{t('modal.upload_feedback_title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-black transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic details */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 text-left">
              {t('modal.customer_details_subtitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.first_name_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('first_name')}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs">{errors.first_name.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">{t('form.last_name_label')}</label>
                <input
                  {...register('last_name')}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs">{errors.last_name.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.email_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs">{errors.email.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.company_name_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('registered_company_name')}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                />
                {errors.registered_company_name && (
                  <p className="text-red-500 text-xs">
                    {errors.registered_company_name.message}
                  </p>
                )}
              </div>

              {/* FIXED: Country Code and Mobile Number now have their own separate labels */}
              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.country_code_label')} <span className="text-red-500">*</span>
                </label>
                <Select
                  placeholder={t('form.select_placeholder')}
                  options={mobile_country_code}
                  value={mobile_country_code?.find((opt) => opt.value === watch('country_code')) || null}
                  onChange={(code) => setValue('country_code', code?.value, { shouldValidate: true })}
                  isClearable
                  className="w-full"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '42px',
                    }),
                  }}
                  components={{
                    Option: CountryOption,
                    SingleValue: CountrySingleValue,
                  }}
                />
                {errors.country_code && (
                  <p className="text-red-500 text-xs">{errors.country_code.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.mobile_number_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('mobile_number')}
                  className="w-full border rounded-lg px-3 py-2 h-[42px] focus:outline-none focus:ring-1 focus:ring-black"
                />
                {errors.mobile_number && (
                  <p className="text-red-500 text-xs">{errors.mobile_number.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Feedback details */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 text-left">
              {t('modal.feedback_subtitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.rating_label')} <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('star_rating')}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
                >
                  {[1, 2, 3, 4, 5].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                {errors.star_rating && (
                  <p className="text-red-500 text-xs">{errors.star_rating.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center justify-between w-full">
                  <label className="text-sm font-medium text-slate-700 text-left"> 
                    {t('form.upload_images_label')}
                  </label>
                  <span className="text-xs text-slate-400">
                    {images.length}/{MAX_IMAGES}
                  </span>
                </div>
                <div className="flex items-center gap-2 w-full">
                  <input
                    id="feedbackImages"
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={images.length >= MAX_IMAGES}
                  />
                  <label
                    htmlFor="feedbackImages"
                    className={`px-4 py-2 h-[42px] border rounded-lg text-sm font-medium flex items-center justify-center gap-2 w-full ${
                      images.length >= MAX_IMAGES
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'cursor-pointer bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                    }`}
                  >
                    <DocumentPlusIcon className="h-4 w-4" />
                    {t('form.choose_files_button')}
                  </label>
                </div>
              </div>
            </div>

            {images.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4">
                {images.map((file, index) => (
                  <div key={index} className="relative w-16 h-16">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-black text-white rounded-full p-0.5"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="md:col-span-2 flex flex-col items-start gap-1 mt-4">
              <label className="text-sm font-medium text-slate-700 text-left">
                {t('form.proof_file_label')} <span className="text-red-500">*</span>
              </label>

              <div className="flex items-center gap-2 w-full">
                <input
                  id="feedbackProofFile"
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={handleProofFileChange}
                />

                <label
                  htmlFor="feedbackProofFile"
                  className="px-4 py-2 h-[42px] border rounded-lg text-sm font-medium flex items-center justify-center gap-2 w-full cursor-pointer bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200"
                >
                  <DocumentPlusIcon className="h-4 w-4" />
                  {t('form.choose_proof_file_button')}
                </label>
              </div>

              {watch('feedback_proof_file') && (
                <div className="mt-3 w-full border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {watch('feedback_proof_file')?.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {(watch('feedback_proof_file')?.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setValue('feedback_proof_file', null, { shouldValidate: true });
                        setProofPreview(null);
                        const input = document.getElementById('feedbackProofFile');
                        if (input) input.value = '';
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {proofPreview && (
                    <img
                      src={proofPreview}
                      alt="Proof preview"
                      className="mt-3 w-24 h-24 object-cover rounded-lg border border-gray-200"
                    />
                  )}
                </div>
              )}

              <p className="text-xs text-slate-400">
                {t('form.allowed_file_types_text')}
              </p>

              {errors.feedback_proof_file && (
                <p className="text-red-500 text-xs">
                  {errors.feedback_proof_file.message}
                </p>
              )}
            </div>

            <div className="flex flex-col items-start gap-1 mt-4 w-full">
              <div className="flex items-center justify-between w-full">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {t('form.description_label')} <span className="text-red-500">*</span>
                </label>
                <span
                  className={`text-xs ${
                    descriptionValue.length > 250 ? 'text-red-500' : 'text-slate-400'
                  }`}
                >
                  {descriptionValue.length}/250
                </span>
              </div>
              <textarea
                rows={4}
                {...register('description')}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
              />
              {errors.description && (
                <p className="text-red-500 text-xs">{errors.description.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
            >
              {t('form.cancel_button')}
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {isSubmitting ? t('form.uploading_button') : t('form.upload_feedback_button')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadFeedbackModal;